// Graphiques de la page Onboarding, en SVG fait main comme ceux des autres sections (JobsCharts) : courbe des
// inscriptions avec la période N-1, colonnes de la distribution avec N-1, et sankey du parcours et du tutoriel.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import js from './JobsStatsSection.module.scss';
import own from './OnboardingPage.module.scss';
import { layoutSankey, shareOf, formatPct, type SankeyGraph, type SankeyKind } from './onboarding';

const PAD_LEFT = 44;
const PAD_TOP = 12;
const PAD_BOTTOM = 26;

/** Largeur mesurée du cadre (au moins `min`). */
function useMeasuredWidth(min = 240) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(Math.max(min, Math.floor(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [min]);
  return [ref, width] as const;
}

/** Maximum arrondi vers le haut (1, 2, 5 × 10^n), au moins 1. */
function niceMax(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(n));
  const f = n / p;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * p;
}

function ticksOf(max: number): number[] {
  return [0, max / 4, max / 2, (3 * max) / 4, max];
}

// ==================== Courbe avec N-1 ====================

export interface LinePoint {
  key: string;
  /** Graduation sous l'axe. */
  tick: string;
  /** Titre de l'infobulle. */
  title: string;
  value: number;
}

/**
 * Courbe d'une série (aire légère et points quand ils sont peu nombreux), et la période N-1 en pointillés, alignée par
 * index. Infobulle au survol ou au clavier.
 */
export function LineCompareChart({ points, previous, ariaLabel, formatValue, height = 220, currentLabel, previousLabel }: {
  points: LinePoint[];
  previous?: LinePoint[] | null;
  ariaLabel: string;
  formatValue: (n: number) => string;
  height?: number;
  currentLabel: string;
  previousLabel: string;
}) {
  const [wrapRef, width] = useMeasuredWidth();
  const [hover, setHover] = useState<number | null>(null);
  const prev = previous && previous.length > 0 ? previous : null;
  const max = niceMax(Math.max(0, ...points.map((p) => p.value), ...(prev ?? []).map((p) => p.value)));
  const plotW = width - PAD_LEFT - 8;
  const plotH = height - PAD_TOP - PAD_BOTTOM;
  const n = Math.max(points.length, prev?.length ?? 0);
  const xOf = (i: number) => PAD_LEFT + (n <= 1 ? plotW / 2 : (i * plotW) / (n - 1));
  const yOf = (v: number) => PAD_TOP + plotH - (v / max) * plotH;
  const pathOf = (series: LinePoint[]) => series.map((p, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(p.value).toFixed(1)}`).join(' ');
  const area = points.length > 1
    ? `${pathOf(points)} L${xOf(points.length - 1).toFixed(1)},${(PAD_TOP + plotH).toFixed(1)} L${xOf(0).toFixed(1)},${(PAD_TOP + plotH).toFixed(1)} Z`
    : '';
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / 64))));
  const showDots = n <= 40;
  const slot = n > 0 ? plotW / Math.max(1, n - 1) : plotW;
  const hovered = hover !== null ? points[hover] : null;
  const hoveredPrev = hover !== null && prev ? prev[hover] : null;
  const hx = hover !== null ? xOf(hover) : 0;

  return (
    <div className={js.chartBlock}>
      <ul className={js.legend}>
        <li><span className={`${js.swatch} ${own.swatchCurrent}`} />{currentLabel}</li>
        {prev && <li><span className={`${js.swatch} ${own.swatchPrevious}`} />{previousLabel}</li>}
      </ul>
      <div ref={wrapRef} className={js.chartWrap} onMouseLeave={() => setHover(null)}>
        <svg width={width} height={height} role="img" aria-label={ariaLabel} className={js.chartSvg}>
          {ticksOf(max).map((t, i) => {
            const y = yOf(t);
            return (
              <g key={i}>
                <line x1={PAD_LEFT} x2={width - 8} y1={y} y2={y} className={js.grid} />
                <text x={PAD_LEFT - 8} y={y + 4} textAnchor="end" className={js.tick}>{formatValue(Math.round(t))}</text>
              </g>
            );
          })}
          {area && <path d={area} className={own.lineArea} />}
          {prev && prev.length > 0 && <path d={pathOf(prev)} className={own.linePrevious} />}
          {points.length > 0 && <path d={pathOf(points)} className={own.lineCurrent} />}
          {showDots && points.map((p, i) => <circle key={p.key} cx={xOf(i)} cy={yOf(p.value)} r={2.5} className={own.dotCurrent} />)}
          {points.map((p, i) => (i % labelEvery === 0 ? (
            <text key={`t${p.key}`} x={xOf(i)} y={height - 8} textAnchor="middle" className={js.tick}>{p.tick}</text>
          ) : null))}
          {hover !== null && <line x1={hx} x2={hx} y1={PAD_TOP} y2={PAD_TOP + plotH} className={own.cursor} />}
          <line x1={PAD_LEFT} x2={width - 8} y1={PAD_TOP + plotH} y2={PAD_TOP + plotH} className={js.axis} />
          {points.map((p, i) => (
            <rect
              key={`h${p.key}`}
              x={xOf(i) - slot / 2}
              y={PAD_TOP}
              width={Math.max(4, slot)}
              height={plotH}
              fill="transparent"
              tabIndex={0}
              aria-label={`${p.title} : ${formatValue(p.value)}`}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
            />
          ))}
        </svg>
        {hovered && (
          <div className={`${js.tooltip} ${hx > width * 0.6 ? js.tooltipLeft : ''}`} style={{ left: hx }}>
            <strong>{hovered.title}</strong>
            <span className={js.tooltipRow}>
              <span className={`${js.swatch} ${own.swatchCurrent}`} />
              <span className={js.tooltipLabel}>{currentLabel}</span>
              <span className={js.tooltipValue}>{formatValue(hovered.value)}</span>
            </span>
            {hoveredPrev && (
              <span className={js.tooltipRow}>
                <span className={`${js.swatch} ${own.swatchPrevious}`} />
                <span className={js.tooltipLabel}>{hoveredPrev.title}</span>
                <span className={js.tooltipValue}>{formatValue(hoveredPrev.value)}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Colonnes avec N-1 ====================

export interface ColumnPoint {
  key: string;
  label: string;
  title: string;
  value: number;
  previous?: number | null;
}

/** Colonnes de la période, et à côté celles de la période N-1 quand `showPrevious`. */
export function CompareColumnChart({ data, showPrevious, ariaLabel, formatValue, height = 220, currentLabel, previousLabel }: {
  data: ColumnPoint[];
  showPrevious: boolean;
  ariaLabel: string;
  formatValue: (n: number) => string;
  height?: number;
  currentLabel: string;
  previousLabel: string;
}) {
  const [wrapRef, width] = useMeasuredWidth();
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...data.map((d) => d.value), ...(showPrevious ? data.map((d) => d.previous ?? 0) : [])));
  const plotW = width - PAD_LEFT - 8;
  const plotH = height - PAD_TOP - PAD_BOTTOM;
  const slot = data.length > 0 ? plotW / data.length : plotW;
  const group = Math.min(56, slot * 0.78);
  const barW = showPrevious ? group / 2 - 1 : group;
  const labelEvery = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotW / 48))));
  const yOf = (v: number) => PAD_TOP + plotH - (v / max) * plotH;
  const hovered = hover !== null ? data[hover] : null;
  const hx = hover !== null ? PAD_LEFT + hover * slot + slot / 2 : 0;

  return (
    <div className={js.chartBlock}>
      {showPrevious && (
        <ul className={js.legend}>
          <li><span className={`${js.swatch} ${own.swatchCurrent}`} />{currentLabel}</li>
          <li><span className={`${js.swatch} ${own.swatchPrevious}`} />{previousLabel}</li>
        </ul>
      )}
      <div ref={wrapRef} className={js.chartWrap} onMouseLeave={() => setHover(null)}>
        <svg width={width} height={height} role="img" aria-label={ariaLabel} className={js.chartSvg}>
          {ticksOf(max).map((t, i) => {
            const y = yOf(t);
            return (
              <g key={i}>
                <line x1={PAD_LEFT} x2={width - 8} y1={y} y2={y} className={js.grid} />
                <text x={PAD_LEFT - 8} y={y + 4} textAnchor="end" className={js.tick}>{formatValue(Math.round(t))}</text>
              </g>
            );
          })}
          {data.map((d, i) => {
            const x0 = PAD_LEFT + i * slot + (slot - group) / 2;
            const hCur = (d.value / max) * plotH;
            const hPrev = ((d.previous ?? 0) / max) * plotH;
            return (
              <g key={d.key} opacity={hover === null || hover === i ? 1 : 0.6}>
                <rect x={x0} y={PAD_TOP + plotH - hCur} width={Math.max(2, barW)} height={hCur} rx={2} className={own.barCurrent} />
                {showPrevious && (
                  <rect x={x0 + barW + 2} y={PAD_TOP + plotH - hPrev} width={Math.max(2, barW)} height={hPrev} rx={2} className={own.barPrevious} />
                )}
                {i % labelEvery === 0 && (
                  <text x={PAD_LEFT + i * slot + slot / 2} y={height - 8} textAnchor="middle" className={js.tick}>{d.label}</text>
                )}
                <rect
                  x={PAD_LEFT + i * slot}
                  y={PAD_TOP}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={`${d.title} : ${formatValue(d.value)}`}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                />
              </g>
            );
          })}
          <line x1={PAD_LEFT} x2={width - 8} y1={PAD_TOP + plotH} y2={PAD_TOP + plotH} className={js.axis} />
        </svg>
        {hovered && (
          <div className={`${js.tooltip} ${hx > width * 0.6 ? js.tooltipLeft : ''}`} style={{ left: hx }}>
            <strong>{hovered.title}</strong>
            <span className={js.tooltipRow}>
              <span className={`${js.swatch} ${own.swatchCurrent}`} />
              <span className={js.tooltipLabel}>{currentLabel}</span>
              <span className={js.tooltipValue}>{formatValue(hovered.value)}</span>
            </span>
            {showPrevious && (
              <span className={js.tooltipRow}>
                <span className={`${js.swatch} ${own.swatchPrevious}`} />
                <span className={js.tooltipLabel}>{previousLabel}</span>
                <span className={js.tooltipValue}>{formatValue(hovered.previous ?? 0)}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Sankey ====================

const NODE_CLASS: Record<SankeyKind, string> = { main: own.nmain, tuto: own.ntuto, lost: own.nlost, stop: own.nstop };
const LINK_CLASS: Record<SankeyKind, string> = { main: own.lmain, tuto: own.ltuto, lost: own.llost, stop: own.lstop };

const NODE_WIDTH = 12;
const LABEL_PAD = 18;

/**
 * Sankey : hauteur des nœuds et largeur des bandes = nombre de joueurs, % = part de la base du graphe. Un nœud qui a
 * des identifiants d'échantillon (`focus`) se clique (souris, Entrée, Espace). Le cadre défile en largeur sur un petit
 * écran au lieu d'écraser les libellés. `labelsBelow` : nœuds dont le libellé passe sous le nœud (les arrêts du tuto,
 * en bas de colonne, dont le libellé déborderait sur la colonne suivante).
 */
export function SankeyChart({ graph, ariaLabel, formatValue, height = 320, minWidth = 760, onNodeClick, labelsBelow = [], hideEmpty = false }: {
  graph: SankeyGraph;
  ariaLabel: string;
  formatValue: (n: number) => string;
  height?: number;
  minWidth?: number;
  onNodeClick?: (focus: string[]) => void;
  labelsBelow?: SankeyKind[];
  /** Nœuds à 0 retirés (sauf la racine) : en échantillon, les branches exclues ne font qu'encombrer. */
  hideEmpty?: boolean;
}) {
  const [wrapRef, measured] = useMeasuredWidth(320);
  const width = Math.max(measured, minWidth);
  const padBottom = labelsBelow.length > 0 ? LABEL_PAD + 30 : LABEL_PAD;
  const innerH = height - LABEL_PAD - padBottom;
  const shown = useMemo<SankeyGraph>(
    () => (hideEmpty ? { ...graph, nodes: graph.nodes.filter((n) => n.column === 0 || n.value > 0) } : graph),
    [graph, hideEmpty],
  );
  const layout = useMemo(
    () => layoutSankey(shown, { width, height: innerH, nodeWidth: NODE_WIDTH, gap: labelsBelow.length > 0 ? 40 : 26, minNodeHeight: 3 }),
    [shown, width, innerH, labelsBelow.length],
  );
  const lastColumn = Math.max(0, ...layout.nodes.map((n) => n.column));
  const labelOf = new Map(graph.nodes.map((n) => [n.id, n.label]));

  const activate = (focus: string[] | undefined) => {
    if (focus && onNodeClick) onNodeClick(focus);
  };
  const onKey = (e: KeyboardEvent<SVGGElement>, focus: string[] | undefined) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate(focus);
    }
  };

  return (
    <div ref={wrapRef} className={own.sankeyFrame}>
      <svg width={width} height={height} role="img" aria-label={ariaLabel} className={own.sankeySvg}>
        <g transform={`translate(0, ${LABEL_PAD})`}>
          {layout.links.map((l) => (
            <path key={`${l.source}>${l.target}`} d={l.path} strokeWidth={l.width} className={`${own.link} ${LINK_CLASS[l.kind]}`}>
              <title>{`${labelOf.get(l.source)} vers ${labelOf.get(l.target)} : ${formatValue(l.value)} joueurs`}</title>
            </path>
          ))}
          {layout.nodes.map((n) => {
            const clickable = !!n.focus && !!onNodeClick;
            const below = labelsBelow.includes(n.kind);
            const right = n.column < lastColumn;
            const anchor = below ? (n.column === 0 ? 'start' : right ? 'middle' : 'end') : right ? 'start' : 'end';
            const tx = below ? (anchor === 'middle' ? n.x + NODE_WIDTH / 2 : anchor === 'start' ? n.x : n.x + NODE_WIDTH)
              : right ? n.x + NODE_WIDTH + 6 : n.x - 6;
            const ty = below ? n.y + Math.max(3, n.h) + 14 : n.y + n.h / 2 - 2;
            const pct = shareOf(n.value, graph.base);
            const sub = `${formatValue(n.value)} · ${formatPct(pct)}`;
            return (
              <g
                key={n.id}
                className={`${own.node} ${clickable ? own.nodeClickable : ''}`}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                aria-label={clickable ? `${n.label} : ${sub}. Ne garder que ces joueurs` : undefined}
                onClick={clickable ? () => activate(n.focus) : undefined}
                onKeyDown={clickable ? (e) => onKey(e, n.focus) : undefined}
              >
                <rect x={n.x} y={n.y} width={NODE_WIDTH} height={Math.max(3, n.h)} className={NODE_CLASS[n.kind]}>
                  <title>{`${n.label} : ${sub}${clickable ? ' (clic : ne garder que ces joueurs)' : ''}`}</title>
                </rect>
                <text x={tx} y={ty} textAnchor={anchor} className={own.nodeLabel}>{n.label}</text>
                <text x={tx} y={ty + 14} textAnchor={anchor} className={own.nodeSub}>{sub}</text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
